import {
  PlatformAdminAssignmentListResponseV1Schema,
  PlatformAdminAssignmentV1Schema,
  type PlatformAdminAssignmentV1,
} from "@fulcrum5/suite-contracts";
import { useCallback, useEffect, useId, useState } from "react";
import { Type } from "typebox";
import { PlatformApiError } from "../api/platformApi.ts";
import { usePlatformSession } from "../session/PlatformSessionProvider.tsx";

const EmptyResponseSchema = Type.Null();
const WORKOS_USER_ID = /^user_[A-Za-z0-9_-]+$/;
const WRAPPABLE_VALUE_STYLE = { minWidth: 0, overflowWrap: "anywhere" } as const;

type ListPhase = "loading" | "ready" | "error";

interface PageError {
  readonly status: number;
  readonly message: string;
  readonly retryable: boolean;
  readonly fieldErrors: Readonly<Record<string, string>> | undefined;
}

function pageError(error: unknown): PageError {
  if (error instanceof PlatformApiError) {
    return {
      status: error.status,
      message: error.message,
      retryable: error.retryable,
      fieldErrors: error.error.fieldErrors,
    };
  }
  return {
    status: 0,
    message: "Platform could not complete this request",
    retryable: true,
    fieldErrors: undefined,
  };
}

function idempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID !== "function") {
    throw new Error("A secure idempotency key could not be created");
  }
  return globalThis.crypto.randomUUID();
}

function canRevoke(
  assignment: PlatformAdminAssignmentV1,
  assignmentCount: number,
): boolean {
  return assignment.canRevoke && assignmentCount > 1;
}

function errorForWorkosUserId(error: PageError | null): string | null {
  if (!error) return null;
  return error.fieldErrors?.workosUserId
    ?? error.fieldErrors?.body
    ?? (error.status === 422 ? error.message : null);
}

function assignmentDescription(assignment: PlatformAdminAssignmentV1): string {
  return assignment.email ?? "No email available";
}

/** F5-only assignment management. Server responses remain the lifecycle authority. */
export function PlatformAdminsPage(): JSX.Element {
  const { api, identity } = usePlatformSession();
  const [phase, setPhase] = useState<ListPhase>("loading");
  const [assignments, setAssignments] = useState<readonly PlatformAdminAssignmentV1[]>([]);
  const [listError, setListError] = useState<PageError | null>(null);
  const [workosUserId, setWorkosUserId] = useState("");
  const [grantError, setGrantError] = useState<PageError | null>(null);
  const [mutationError, setMutationError] = useState<PageError | null>(null);
  const [isGranting, setIsGranting] = useState(false);
  const [busyAssignmentId, setBusyAssignmentId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const inputId = useId();
  const grantErrorId = `${inputId}-error`;
  const isF5Admin = identity?.isF5Admin === true;

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    setPhase("loading");
    setListError(null);
    try {
      const response = await api.request({
        path: "/api/admin/platform-admins",
        responseSchema: PlatformAdminAssignmentListResponseV1Schema,
        signal,
      });
      if (signal?.aborted) return;
      setAssignments(response.data.assignments);
      setPhase("ready");
    } catch (error) {
      if (signal?.aborted) return;
      setListError(pageError(error));
      setPhase("error");
    }
  }, [api]);

  useEffect(() => {
    if (!isF5Admin) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [isF5Admin, load]);

  const grantFieldError = errorForWorkosUserId(grantError);

  async function grant(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (isGranting) return;
    const normalizedWorkosUserId = workosUserId.trim();
    setMutationError(null);
    setStatusMessage(null);
    if (!WORKOS_USER_ID.test(normalizedWorkosUserId)) {
      setGrantError({
        status: 422,
        message: "Enter a valid WorkOS user ID",
        retryable: false,
        fieldErrors: { workosUserId: "Enter a valid WorkOS user ID" },
      });
      return;
    }

    setGrantError(null);
    setIsGranting(true);
    try {
      await api.request({
        path: "/api/admin/platform-admins",
        method: "POST",
        body: { version: 1, workosUserId: normalizedWorkosUserId },
        headers: { "idempotency-key": idempotencyKey() },
        responseSchema: PlatformAdminAssignmentV1Schema,
      });
      setWorkosUserId("");
      setStatusMessage("Platform administrator granted.");
      await load();
    } catch (error) {
      const failure = pageError(error);
      setGrantError(failure);
      if (!errorForWorkosUserId(failure)) {
        setMutationError(failure);
      }
    } finally {
      setIsGranting(false);
    }
  }

  async function revoke(assignment: PlatformAdminAssignmentV1): Promise<void> {
    if (!canRevoke(assignment, assignments.length) || busyAssignmentId !== null) return;
    setMutationError(null);
    setStatusMessage(null);
    setBusyAssignmentId(assignment.id);
    try {
      await api.request({
        path: `/api/admin/platform-admins/${assignment.id}`,
        method: "DELETE",
        headers: { "idempotency-key": idempotencyKey() },
        responseSchema: EmptyResponseSchema,
      });
      setStatusMessage("Platform administrator revoked.");
      await load();
    } catch (error) {
      setMutationError(pageError(error));
    } finally {
      setBusyAssignmentId(null);
    }
  }

  if (!isF5Admin) {
    return (
      <section className="platform-page platform-admins-page" aria-labelledby="platform-admins-page-title">
        <p className="platform-page__eyebrow">Fulcrum5 administration</p>
        <h1 id="platform-admins-page-title">Platform administrators</h1>
        <p className="platform-page__summary">Manage explicit Fulcrum5 administrator assignments.</p>
        <p className="platform-admins-page__denied" role="alert">
          This workspace is only available to Fulcrum5 administrators.
        </p>
      </section>
    );
  }

  return (
    <section className="platform-page platform-admins-page" aria-labelledby="platform-admins-page-title">
      <header className="platform-admins-page__header">
        <div className="platform-admins-page__heading" style={WRAPPABLE_VALUE_STYLE}>
          <p className="platform-page__eyebrow">Fulcrum5 administration</p>
          <h1 id="platform-admins-page-title">Platform administrators</h1>
          <p className="platform-page__summary">
            Manage explicit Fulcrum5 administrator assignments. Client organization roles do not grant this access.
          </p>
        </div>
      </header>

      <form className="platform-admins-page__grant" onSubmit={grant} noValidate>
        <div className="platform-admins-page__field" style={WRAPPABLE_VALUE_STYLE}>
          <label htmlFor={inputId}>WorkOS user ID</label>
          <input
            id={inputId}
            className="platform-admins-page__input"
            value={workosUserId}
            onChange={(event) => setWorkosUserId(event.target.value)}
            aria-invalid={grantFieldError ? true : undefined}
            aria-describedby={grantFieldError ? grantErrorId : undefined}
            autoComplete="off"
            disabled={isGranting}
          />
          {grantFieldError ? (
            <p className="platform-admins-page__field-error" id={grantErrorId} role="alert">
              {grantFieldError}
            </p>
          ) : null}
        </div>
        <button className="platform-admins-page__grant-button" type="submit" disabled={isGranting}>
          {isGranting ? "Granting administrator" : "Grant administrator"}
        </button>
      </form>

      {statusMessage ? (
        <p className="platform-admins-page__status" role="status" aria-live="polite">
          {statusMessage}
        </p>
      ) : null}
      {mutationError ? (
        <p className="platform-admins-page__mutation-error" role="alert">
          {mutationError.message}
        </p>
      ) : null}

      <section className="platform-admins-page__list" aria-labelledby="platform-admins-list-title">
        <div className="platform-admins-page__list-heading">
          <h2 id="platform-admins-list-title">Assigned administrators</h2>
          <span aria-live="polite">{phase === "loading" ? "Loading assignments" : `${assignments.length} assigned`}</span>
        </div>

        {phase === "loading" ? (
          <p className="platform-admins-page__loading" aria-live="polite">Loading administrator assignments.</p>
        ) : null}
        {phase === "error" && listError ? (
          <div className="platform-admins-page__load-error" role="alert">
            <p>{listError.message}</p>
            {listError.retryable ? (
              <button className="platform-admins-page__retry" type="button" onClick={() => void load()}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}
        {phase === "ready" && assignments.length === 0 ? (
          <p className="platform-admins-page__empty">No platform administrators are assigned.</p>
        ) : null}
        {phase === "ready" && assignments.length > 0 ? (
          <ul className="platform-admins-page__assignments" aria-label="Platform administrator assignments">
            {assignments.map((assignment) => {
              const revocable = canRevoke(assignment, assignments.length);
              const isBusy = busyAssignmentId === assignment.id;
              const reason = assignmentCountReason(assignment, assignments.length);
              return (
                <li className="platform-admins-page__assignment" key={assignment.id} aria-busy={isBusy || undefined}>
                  <div className="platform-admins-page__assignment-details" style={WRAPPABLE_VALUE_STYLE}>
                    <p className="platform-admins-page__assignment-name" style={WRAPPABLE_VALUE_STYLE}>{assignment.displayName}</p>
                    <p className="platform-admins-page__assignment-email" style={WRAPPABLE_VALUE_STYLE}>{assignmentDescription(assignment)}</p>
                    <p className="platform-admins-page__assignment-role">Fulcrum5 administrator</p>
                  </div>
                  <div className="platform-admins-page__assignment-action" style={WRAPPABLE_VALUE_STYLE}>
                    <button
                      className="platform-admins-page__revoke"
                      type="button"
                      aria-label={`Revoke ${assignment.displayName}`}
                      disabled={!revocable || isBusy}
                      onClick={() => void revoke(assignment)}
                    >
                      {isBusy ? "Revoking" : "Revoke"}
                    </button>
                    {reason ? <span className="platform-admins-page__assignment-reason">{reason}</span> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </section>
  );
}

function assignmentCountReason(
  assignment: PlatformAdminAssignmentV1,
  assignmentCount: number,
): string | null {
  if (assignmentCount <= 1) return "Final administrator";
  if (!assignment.canRevoke) return "Revoke unavailable";
  return null;
}
